import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-calendar/dist/Calendar.css';
import {
  CalendarWrap,
  SmallButton,
  SmallCalWrap,
  CalOrangeBtn,
  CalOrangeWrap,
  CalClockWrap,
  Buttons,
  CalendarExpandPanel,
  CalClockReadonlyBar,
} from 'components/Calendar/Calendar.styled';
import Icons from 'images/allSvgFinTrack.svg';
import { getDate } from 'redux/calendar/calendarSlice';
import { useTranslation } from 'react-i18next';

function stripCalendar(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function calendarsEqualDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function mergeTimeOntoDay(dayAnchor, timeSource) {
  const out = new Date(dayAnchor);
  out.setHours(
    timeSource.getHours(),
    timeSource.getMinutes(),
    timeSource.getSeconds(),
    timeSource.getMilliseconds()
  );
  return out;
}

function formatClockDisplay(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) {
    return '';
  }
  return format(d, 'dd.MM.yyyy HH:mm');
}

function formatDateDisplay(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) {
    return '';
  }
  return format(d, 'dd.MM.yyyy');
}

/** Время (input или список): библиотека может подставить «сегодня», сохраняем календарный день из prev. */
function fixTimeInputKeepsWrongDay(next, prev) {
  if (!next || !prev) return next;
  if (calendarsEqualDay(next, prev)) return next;
  const today = stripCalendar(new Date());
  const nextDay = stripCalendar(next);
  const prevDay = stripCalendar(prev);
  if (
    today &&
    nextDay &&
    prevDay &&
    calendarsEqualDay(nextDay, today) &&
    !calendarsEqualDay(prevDay, today)
  ) {
    return mergeTimeOntoDay(prevDay, next);
  }
  return next;
}

export const Calendar = ({
  value,
  changeValue,
  /** По умолчанию вкл.: все текущие встраивания — в модалках; передайте false для выпадающего popper на странице */
  expandSmoothInModal = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const pickerScheme =
    theme?.ui?.scheme === 'dark' ? 'dark' : 'light';
  const openFromIcon = () => {
    setIsOpen(prevState => !prevState);
  };
  const { t } = useTranslation();

  const footerButtons = (
    <div>
      <Buttons
        onClick={() => {
          setIsOpen(false);
        }}
      >
        {t('calendar.btnCansel')}
      </Buttons>
      <Buttons
        onClick={() => {
          setIsOpen(false);
        }}
      >
        {t('calendar.btnOk')}
      </Buttons>
    </div>
  );

  const basePickerProps = {
    showPopperArrow: false,
    selected: value,
    shouldCloseOnSelect: false,
    dateFormat: 'dd.MM.yyyy',
    onChange: date => changeValue(date),
    calendarClassName: `fintrack-datepicker-cal fintrack-datepicker-cal--${pickerScheme}`,
  };

  const dateOnlyShellCls = `fintrack-datepicker-popper-clock fintrack-datepicker-popper-clock--${pickerScheme} fintrack-datepicker-inline--dateonly`;

  if (expandSmoothInModal) {
    return (
      <>
        <CalendarWrap>
          <SmallButton
            type="button"
            onClick={openFromIcon}
            aria-expanded={isOpen}
          >
            <svg width="16" height="16" fill={'#FF7601'}>
              <use href={`${Icons}#icon-calendar`}></use>
            </svg>
          </SmallButton>
          <CalClockReadonlyBar type="button" onClick={openFromIcon}>
            <span>{formatDateDisplay(value) || '—'}</span>
          </CalClockReadonlyBar>
        </CalendarWrap>
        <CalendarExpandPanel $open={isOpen}>
          <div
            className={dateOnlyShellCls}
            style={{ width: '100%' }}
          >
            <DatePicker {...basePickerProps} inline>
              {footerButtons}
            </DatePicker>
          </div>
        </CalendarExpandPanel>
      </>
    );
  }

  return (
    <CalendarWrap>
      <SmallButton type="button" onClick={openFromIcon}>
        <svg width="16" height="16" fill={'#FF7601'}>
          <use href={`${Icons}#icon-calendar`}></use>
        </svg>
      </SmallButton>

      <SmallCalWrap>
        <DatePicker
          {...basePickerProps}
          popperClassName={`fintrack-datepicker-popper fintrack-datepicker-popper-clock fintrack-datepicker-popper-clock--${pickerScheme} fintrack-datepicker-inline--dateonly`}
          onClickOutside={() => setIsOpen(false)}
          open={isOpen}
          readOnly
        >
          {footerButtons}
        </DatePicker>
      </SmallCalWrap>
    </CalendarWrap>
  );
};

export const CalendarOrange = ({ value, changeValue }) => {
  const dispatch = useDispatch();
  const prevValueRef = useRef();

  useEffect(() => {
    const prevValue = prevValueRef.current;
    const year = String(value.getFullYear());
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    const date = {
      year,
      month,
      day,
    };

    if (!prevValue || JSON.stringify(date) !== JSON.stringify(prevValue)) {
      dispatch(getDate(date));
    }

    prevValueRef.current = date;
  }, [dispatch, value]);

  const [isOpen, setIsOpen] = useState(false);
  const openFromIcon = () => {
    setIsOpen(prevState => !prevState);
  };

  return (
    <CalendarWrap>
      <CalOrangeBtn type="button" onClick={openFromIcon}>
        <svg width="16" height="16" fill={'#FF7601'}>
          <use href={`${Icons}#icon-calendar`}></use>
        </svg>
      </CalOrangeBtn>

      <CalOrangeWrap onClick={openFromIcon}>
        <DatePicker
          showPopperArrow={false}
          onClickOutside={() => setIsOpen(false)}
          open={isOpen}
          selected={value}
          shouldCloseOnSelect={false}
          dateFormat="dd.MM.yyyy"
          readOnly
          onChange={date => changeValue(date)}
        ></DatePicker>
      </CalOrangeWrap>
    </CalendarWrap>
  );
};

export const CalendarWithClock = ({
  value,
  changeValue,
  /** По умолчанию вкл.: разворот под полем как в модалках; false — только popper */
  expandSmoothInModal = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  /** Следующий onChange после явного выбора «сегодня» не коэрцим — иначе снова затянули бы старый день. */
  const skipFixNextChangeRef = useRef(false);
  const theme = useTheme();
  const pickerScheme =
    theme?.ui?.scheme === 'dark' ? 'dark' : 'light';

  const openFromIcon = () => {
    setIsOpen(prevState => !prevState);
  };
  const { t } = useTranslation();

  const handleSelectCalendarDate = date => {
    const today = stripCalendar(new Date());
    const picked = stripCalendar(date);
    if (
      picked &&
      today &&
      calendarsEqualDay(picked, today)
    ) {
      skipFixNextChangeRef.current = true;
    }
  };

  const handleChangeDateTime = date => {
    if (!date) {
      changeValue(date);
      return;
    }

    if (skipFixNextChangeRef.current) {
      skipFixNextChangeRef.current = false;
      changeValue(date);
      return;
    }

    const next =
      value != null ? fixTimeInputKeepsWrongDay(date, value) : date;
    changeValue(next);
  };

  const footerButtons = (
    <div>
      <Buttons
        onClick={() => {
          setIsOpen(false);
        }}
      >
        {t('calendar.btnCansel')}
      </Buttons>
      <Buttons
        onClick={() => {
          setIsOpen(false);
        }}
      >
        {t('calendar.btnOk')}
      </Buttons>
    </div>
  );

  const basePickerProps = {
    showPopperArrow: false,
    selected: value,
    shouldCloseOnSelect: false,
    dateFormat: 'dd.MM.yyyy HH:mm',
    onSelect: handleSelectCalendarDate,
    onChange: handleChangeDateTime,
    showTimeSelect: true,
    timeFormat: 'HH:mm',
    timeIntervals: 1,
    timeCaption: t('calendar.timeCaption'),
    calendarClassName: `fintrack-datepicker-cal fintrack-datepicker-cal--${pickerScheme}`,
  };

  if (expandSmoothInModal) {
    return (
      <>
        <CalendarWrap>
          <SmallButton type="button" onClick={openFromIcon} aria-expanded={isOpen}>
            <svg width="16" height="16" fill={'#FF7601'}>
              <use href={`${Icons}#icon-calendar`}></use>
            </svg>
          </SmallButton>
          <CalClockReadonlyBar type="button" onClick={openFromIcon}>
            <span>{formatClockDisplay(value) || '—'}</span>
          </CalClockReadonlyBar>
        </CalendarWrap>
        <CalendarExpandPanel $open={isOpen}>
          <div
            className={`fintrack-datepicker-popper-clock fintrack-datepicker-popper-clock--${pickerScheme}`}
            style={{ width: '100%' }}
          >
            <DatePicker {...basePickerProps} inline>
              {footerButtons}
            </DatePicker>
          </div>
        </CalendarExpandPanel>
      </>
    );
  }

  return (
    <CalendarWrap>
      <SmallButton type="button" onClick={openFromIcon}>
        <svg width="16" height="16" fill={'#FF7601'}>
          <use href={`${Icons}#icon-calendar`}></use>
        </svg>
      </SmallButton>

      <CalClockWrap>
        <DatePicker
          {...basePickerProps}
          popperClassName={`fintrack-datepicker-popper fintrack-datepicker-popper-clock fintrack-datepicker-popper-clock--${pickerScheme}`}
          onClickOutside={() => setIsOpen(false)}
          open={isOpen}
          readOnly
        >
          {footerButtons}
        </DatePicker>
      </CalClockWrap>
    </CalendarWrap>
  );
};
